import React, { useState, useEffect } from 'react'
import styled from 'styled-components'

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.open ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const Modal = styled.div`
  width: 600px;
  max-width: 92vw;
  max-height: 90vh;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  overflow-y: auto;
`

const Header = styled.div`
  padding: 16px 20px;
  font-weight: 600;
  font-size: 18px;
  border-bottom: 1px solid #eee;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  position: sticky;
  top: 0;
  z-index: 1;
`

const Body = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const Footer = styled.div`
  padding: 12px 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  border-top: 1px solid #eee;
  background-color: #f8f9fa;
  position: sticky;
  bottom: 0;
`

const TabContainer = styled.div`
  display: flex;
  gap: 8px;
  border-bottom: 2px solid #e0e0e0;
`

const Tab = styled.button`
  padding: 10px 20px;
  border: none;
  background: ${props => props.$active ? '#667eea' : 'transparent'};
  color: ${props => props.$active ? 'white' : '#666'};
  font-weight: 600;
  cursor: pointer;
  border-radius: 8px 8px 0 0;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$active ? '#5568d3' : '#f0f0f0'};
  }
`

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 6px;
  display: block;
`

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`

const Textarea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  min-height: 100px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  border: 1px solid #ddd;
  background: ${p => p.variant === 'primary' ? '#667eea' : '#fff'};
  color: ${p => p.variant === 'primary' ? '#fff' : '#333'};
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${p => p.variant === 'primary' ? '#5568d3' : '#f5f5f5'};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const ObjectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 12px;
  max-height: 300px;
  overflow-y: auto;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
`

const ObjectCard = styled.div`
  aspect-ratio: 1;
  border: 2px solid ${props => props.$selected ? '#667eea' : '#e0e0e0'};
  border-radius: 8px;
  background: ${props => props.$selected ? '#f0f4ff' : 'white'};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px;
  transition: all 0.2s;

  &:hover {
    border-color: #667eea;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  img {
    width: 60px;
    height: 60px;
    object-fit: contain;
    margin-bottom: 4px;
  }

  span {
    font-size: 11px;
    text-align: center;
    color: #333;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
  }
`

const InfoBox = styled.div`
  padding: 12px;
  background: #fff3cd;
  border: 1px solid #ffecb5;
  border-radius: 8px;
  font-size: 13px;
  color: #856404;
`

export default function AddObjectToProblemSetModal({
  open,
  onClose,
  problemSetId,
  problemSetTitle,
  onSuccess
}) {
  const [activeTab, setActiveTab] = useState('existing') // 'existing' or 'new'
  const [saving, setSaving] = useState(false)

  // 기존 오브젝트 선택 탭
  const [categories, setCategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState('')
  const [categoryObjects, setCategoryObjects] = useState([])
  const [selectedObjectId, setSelectedObjectId] = useState(null)
  const [loadingObjects, setLoadingObjects] = useState(false)

  // 새 오브젝트 생성 탭
  const [newObjectName, setNewObjectName] = useState('')
  const [newObjectCategoryId, setNewObjectCategoryId] = useState('')
  const [personaPrompt, setPersonaPrompt] = useState('')

  // 파일 업로드 관련
  const [imageFile, setImageFile] = useState(null)
  const [uploading, setUploading] = useState(false)

  // 카테고리 목록 로드
  useEffect(() => {
    if (open) {
      loadCategories()
    }
  }, [open])

  // 선택된 카테고리의 오브젝트 목록 로드
  useEffect(() => {
    if (selectedCategory) {
      loadCategoryObjects(selectedCategory)
    }
  }, [selectedCategory])

  const loadCategories = async () => {
    try {
      const response = await fetch('/categories/categories')
      const data = await response.json()
      if (data.success) {
        // 동일한 name끼리 합치기 - 모든 category_id 저장
        const uniqueCategories = {}
        data.categories.forEach(cat => {
          if (!uniqueCategories[cat.name]) {
            uniqueCategories[cat.name] = {
              ...cat,
              allCategoryIds: [cat.category_id]
            }
          } else {
            uniqueCategories[cat.name].allCategoryIds.push(cat.category_id)
          }
        })
        setCategories(Object.values(uniqueCategories))
      }
    } catch (error) {
      console.error('카테고리 로드 실패:', error)
    }
  }

  const loadCategoryObjects = async (categoryId) => {
    setLoadingObjects(true)
    try {
      // 선택된 카테고리의 모든 category_id에 대한 오브젝트 가져오기
      const selectedCat = categories.find(c => c.category_id === parseInt(categoryId))
      if (!selectedCat) return

      const allCategoryIds = selectedCat.allCategoryIds || [selectedCat.category_id]

      // 모든 category_id의 오브젝트를 병렬로 가져오기
      const promises = allCategoryIds.map(id =>
        fetch(`/ObjectUpload/objects-by-category/${id}`).then(r => r.json())
      )

      const results = await Promise.all(promises)

      // 모든 오브젝트를 하나로 합치기 (중복 제거)
      const allObjects = []
      const seenIds = new Set()

      results.forEach(data => {
        if (data.success && data.objects) {
          data.objects.forEach(obj => {
            if (!seenIds.has(obj.object_id)) {
              seenIds.add(obj.object_id)
              allObjects.push(obj)
            }
          })
        }
      })

      setCategoryObjects(allObjects)
    } catch (error) {
      console.error('오브젝트 로드 실패:', error)
    } finally {
      setLoadingObjects(false)
    }
  }

  const handleAddExistingObject = async () => {
    if (!selectedObjectId) {
      alert('오브젝트를 선택해주세요.')
      return
    }

    setSaving(true)
    try {
      const response = await fetch('/auth/add-object-to-problem-set', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          problemSetId,
          arObjectId: selectedObjectId
        })
      })

      const data = await response.json()
      console.log('🔍 오브젝트 추가 응답:', data)
      if (data.success) {
        alert('✅ 오브젝트가 문제 세트에 추가되었습니다!')
        console.log('✅ onSuccess 콜백 호출 중...')
        if (onSuccess) {
          await onSuccess()
          console.log('✅ onSuccess 콜백 완료')
        } else {
          console.warn('⚠️ onSuccess 콜백이 없습니다')
        }
        handleClose()
      } else {
        alert('❌ 추가 실패: ' + data.error)
      }
    } catch (error) {
      console.error('오브젝트 추가 오류:', error)
      alert('오브젝트 추가 중 오류가 발생했습니다.')
    } finally {
      setSaving(false)
    }
  }

  const uploadFile = async (file) => {
    const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    const formData = new FormData()
    formData.append('file', file)  // 'files'가 아니라 'file'
    formData.append('uploadId', uploadId)

    const response = await fetch('/ObjectUpload/upload', {
      method: 'POST',
      body: formData
    })

    const data = await response.json()
    console.log('📤 업로드 응답:', data)

    if (data.success && data.data?.data?.files?.[0]?.url) {
      return data.data.data.files[0].url
    }
    throw new Error('파일 업로드 실패')
  }

  const handleCreateAndAddObject = async () => {
    if (!newObjectName || !newObjectCategoryId) {
      alert('이름과 카테고리는 필수입니다.')
      return
    }

    if (!imageFile) {
      alert('이미지 파일을 선택해주세요.')
      return
    }

    setSaving(true)
    setUploading(true)
    try {
      // 이미지 파일 업로드
      const imageUrl = await uploadFile(imageFile)

      // 1. 새 오브젝트 생성 (GLB는 null, 나중에 ObjectUploadPage에서 업로드)
      const createResponse = await fetch('/ObjectUpload/create-object', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          categoryId: newObjectCategoryId,
          name: newObjectName,
          imageUrl: imageUrl,
          personaPrompt: personaPrompt || null  // 페르소나 프롬프트 추가
        })
      })

      const createData = await createResponse.json()
      if (!createData.success) {
        throw new Error(createData.error || '오브젝트 생성 실패')
      }

      const newObjectId = createData.data.object_id

      // 2. 문제 세트에 추가
      const addResponse = await fetch('/auth/add-object-to-problem-set', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          problemSetId,
          arObjectId: newObjectId
        })
      })

      const addData = await addResponse.json()
      if (addData.success) {
        alert('✅ 새 오브젝트가 생성되고 문제 세트에 추가되었습니다!\n\n💡 이제 오브젝트를 선택하고 GLB 파일을 업로드해주세요.')
        onSuccess && onSuccess()
        handleClose()
      } else {
        alert('❌ 추가 실패: ' + addData.error)
      }
    } catch (error) {
      console.error('오브젝트 생성/추가 오류:', error)
      alert('오류: ' + error.message)
    } finally {
      setSaving(false)
      setUploading(false)
    }
  }

  const handleClose = () => {
    if (!saving) {
      setActiveTab('existing')
      setSelectedCategory('')
      setCategoryObjects([])
      setSelectedObjectId(null)
      setNewObjectName('')
      setNewObjectCategoryId('')
      setImageFile(null)
      onClose()
    }
  }

  return (
    <Backdrop open={open} onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <Modal>
        <Header>📝 "{problemSetTitle}"에 오브젝트 추가</Header>

        <Body>
          <TabContainer>
            <Tab $active={activeTab === 'existing'} onClick={() => setActiveTab('existing')}>
              기존 오브젝트 선택
            </Tab>
            <Tab $active={activeTab === 'new'} onClick={() => setActiveTab('new')}>
              새 오브젝트 생성
            </Tab>
          </TabContainer>

          {activeTab === 'existing' ? (
            <>
              <div>
                <Label>카테고리 선택</Label>
                <Select
                  value={selectedCategory}
                  onChange={(e) => {
                    setSelectedCategory(e.target.value)
                    setSelectedObjectId(null)
                  }}
                  disabled={saving}
                >
                  <option value="">카테고리를 선택하세요</option>
                  {categories.map(cat => (
                    <option key={cat.category_id} value={cat.category_id}>
                      {cat.name}
                    </option>
                  ))}
                </Select>
              </div>

              {selectedCategory && (
                <div>
                  <Label>오브젝트 선택</Label>
                  {loadingObjects ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                      로딩 중...
                    </div>
                  ) : categoryObjects.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                      이 카테고리에 오브젝트가 없습니다.
                    </div>
                  ) : (
                    <ObjectGrid>
                      {categoryObjects.map(obj => (
                        <ObjectCard
                          key={obj.object_id}
                          $selected={selectedObjectId === obj.object_id}
                          onClick={() => setSelectedObjectId(obj.object_id)}
                        >
                          {obj.image_url && <img src={obj.image_url} alt={obj.name} />}
                          <span>{obj.name}</span>
                        </ObjectCard>
                      ))}
                    </ObjectGrid>
                  )}
                </div>
              )}

              <InfoBox>
                💡 기존에 등록된 오브젝트를 선택하여 이 문제 세트에 추가합니다.
              </InfoBox>
            </>
          ) : (
            <>
              <div>
                <Label>오브젝트 이름 *</Label>
                <Input
                  value={newObjectName}
                  onChange={(e) => setNewObjectName(e.target.value)}
                  placeholder="예: 이상해씨"
                  disabled={saving}
                />
              </div>

              <div>
                <Label>카테고리 *</Label>
                <Select
                  value={newObjectCategoryId}
                  onChange={(e) => setNewObjectCategoryId(e.target.value)}
                  disabled={saving}
                >
                  <option value="">카테고리를 선택하세요</option>
                  {categories.map(cat => (
                    <option key={cat.category_id} value={cat.category_id}>
                      {cat.name}
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <Label>이미지 파일 *</Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setImageFile(e.target.files[0])}
                  disabled={saving || uploading}
                />
                {imageFile && (
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    선택됨: {imageFile.name}
                  </div>
                )}
              </div>

              <div>
                <Label>AI 페르소나 프롬프트 (선택)</Label>
                <Textarea
                  value={personaPrompt}
                  onChange={(e) => setPersonaPrompt(e.target.value)}
                  placeholder="예: 당신은 대한민국의 독립운동가 안중근입니다. 1909년 하얼빈에서 이토 히로부미를 저격한 의사입니다. 당신의 독립에 대한 열정과 신념을 바탕으로 학생들의 질문에 답변해주세요."
                  disabled={saving}
                />
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  💡 이 오브젝트가 사람(역사 인물 등)인 경우, Claude AI가 해당 인물로 학생들과 대화할 수 있도록 페르소나를 설정하세요.
                </div>
              </div>

              {uploading && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#e3f2fd',
                  border: '1px solid #90caf9',
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: '#1976d2',
                  textAlign: 'center'
                }}>
                  ⏳ 이미지 업로드 중...
                </div>
              )}

              <InfoBox>
                💡 <strong>이미지</strong>를 업로드하여 새 오브젝트를 생성합니다.<br/>
                <strong>GLB 파일</strong>은 이후 오브젝트 선택 후 업로드해주세요.
              </InfoBox>
            </>
          )}
        </Body>

        <Footer>
          <Button onClick={handleClose} disabled={saving}>
            취소
          </Button>
          <Button
            variant="primary"
            onClick={activeTab === 'existing' ? handleAddExistingObject : handleCreateAndAddObject}
            disabled={saving || (activeTab === 'existing' ? !selectedObjectId : !newObjectName || !newObjectCategoryId)}
          >
            {saving ? '추가 중...' : '추가'}
          </Button>
        </Footer>
      </Modal>
    </Backdrop>
  )
}
